import { useState, useEffect } from 'react'
import {
  Download,
  TrendingUp,
  Users,
  Video,
  DollarSign,
  BarChart3,
  Wallet,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface PerformanceRow {
  interviewer: string
  sessions: number
  avgRating: number
  clientSatisfaction: number
  revenue: string
  payout: string
}

interface InterviewerMetric {
  interviewer: string
  sessionsCompleted: number
  cancelRate: string
  avgSessionLength: string
  clientRetention: string
  totalHours: number
}

export default function AdminReports() {
  const [dateRange, setDateRange] = useState('month')
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [revenueData, setRevenueData] = useState<PerformanceRow[]>([])
  const [interviewerMetrics, setInterviewerMetrics] = useState<InterviewerMetric[]>([])
  const [stats, setStats] = useState({
    totalSessions: 0,
    revenue: '$0',
    payouts: '$0',
    newClients: 0,
    avgRating: '0.0',
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [revenueRes, interviewerRes] = await Promise.all([
          adminApi.getRevenueReport({ range: dateRange }).catch(() => null),
          adminApi.getInterviewerReport().catch(() => null),
        ])

        if (revenueRes) {
          setStats({
            totalSessions: revenueRes.total_sessions || 0,
            revenue: revenueRes.total_revenue || '$0',
            payouts: revenueRes.total_payouts || '$0',
            newClients: revenueRes.new_clients || 0,
            avgRating: revenueRes.avg_rating || '0.0',
          })
          setRevenueData(revenueRes.by_interviewer || [])
        }

        if (interviewerRes) {
          setInterviewerMetrics(interviewerRes || [])
        }
      } catch (err) {
        console.error('Failed to fetch report data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [dateRange])

  if (loading) {
    return <div className="loading-screen">Loading reports...</div>
  }

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">
            Performance insights and export options
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <select
            className="form-input"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ width: 'auto', padding: '8px 12px' }}
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          <button className="btn btn-primary">
            <Download size={18} />
            Export Report
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon primary">
            <Video size={24} />
          </div>
          <div className="value">{stats.totalSessions}</div>
          <div className="label">Total Sessions</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <DollarSign size={24} />
          </div>
          <div className="value">{stats.revenue}</div>
          <div className="label">Revenue</div>
        </div>
        <div className="stat-card">
          <div className="icon warning">
            <Wallet size={24} />
          </div>
          <div className="value">{stats.payouts}</div>
          <div className="label">Interviewer Payouts</div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <Users size={24} />
          </div>
          <div className="value">{stats.newClients}</div>
          <div className="label">New Clients</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <TrendingUp size={24} />
          </div>
          <div className="value">{stats.avgRating}</div>
          <div className="label">Avg Rating</div>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="tabs" style={{ marginBottom: 24 }}>
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'interviewer-performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('interviewer-performance')}
        >
          <TrendingUp size={16} />
          Interviewer Performance
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>
              Revenue & Payouts
            </h3>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
            >
              <Download size={14} />
              CSV
            </button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Interviewer</th>
                  <th>Sessions</th>
                  <th>Avg Rating</th>
                  <th>Satisfaction</th>
                  <th>Revenue</th>
                  <th>Payout</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: 'center',
                        padding: 40,
                        color: 'var(--text-muted)',
                      }}
                    >
                      No revenue data available for this period
                    </td>
                  </tr>
                ) : (
                  revenueData.map((data, index) => (
                    <tr key={index}>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div className="avatar">
                            {data.interviewer.charAt(0)}
                          </div>
                          <span>{data.interviewer}</span>
                        </div>
                      </td>
                      <td>{data.sessions}</td>
                      <td>
                        <span style={{ color: '#d4af37' }}>
                          {data.avgRating}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 50,
                              height: 6,
                              background: 'var(--border)',
                              borderRadius: 3,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${data.clientSatisfaction}%`,
                                background: 'var(--success)',
                                borderRadius: 3,
                              }}
                            />
                          </div>
                          <span style={{ fontSize: 13 }}>
                            {data.clientSatisfaction}%
                          </span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{data.revenue}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>
                        {data.payout}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'interviewer-performance' && (
        <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>
              Interviewer Performance Metrics
            </h3>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: 13 }}
            >
              <Download size={14} />
              Export
            </button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Interviewer</th>
                  <th>Sessions</th>
                  <th>Cancel Rate</th>
                  <th>Avg Length</th>
                  <th>Client Retention</th>
                  <th>Total Hours</th>
                </tr>
              </thead>
              <tbody>
                {interviewerMetrics.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        textAlign: 'center',
                        padding: 40,
                        color: 'var(--text-muted)',
                      }}
                    >
                      No interviewer data available
                    </td>
                  </tr>
                ) : (
                  interviewerMetrics.map((data, index) => (
                    <tr key={index}>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div className="avatar">
                            {data.interviewer.charAt(0)}
                          </div>
                          <span>{data.interviewer}</span>
                        </div>
                      </td>
                      <td>{data.sessionsCompleted}</td>
                      <td>{data.cancelRate}</td>
                      <td>{data.avgSessionLength}</td>
                      <td>{data.clientRetention}</td>
                      <td>{data.totalHours}h</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
